import Badge from '@/components/public/ui/Badge';
import Icon from '@/components/public/ui/Icon';
import { trackUmamiEvent } from '@/lib/analytics';

export interface DocumentItem {
  id: number;
  title: string;
  description: string | null;
  category: string;
  year: number;
  date: string | null;
  fileSize: string | null;
  url: string | null;
}

// Uniform surface card; category identity lives in the icon tile + Badge.
const categoryConfig: Record<
  string,
  { icon: string; badge: 'primary' | 'secondary' | 'tertiary'; tintBg: string; tintText: string }
> = {
  Atas: {
    icon: 'description',
    badge: 'primary',
    tintBg: 'bg-primary/12',
    tintText: 'text-primary',
  },
  Editais: {
    icon: 'campaign',
    badge: 'tertiary',
    tintBg: 'bg-tertiary/12',
    tintText: 'text-tertiary',
  },
  Regulamentos: {
    icon: 'gavel',
    badge: 'secondary',
    tintBg: 'bg-secondary/15',
    tintText: 'text-secondary',
  },
  Contas: {
    icon: 'account_balance',
    badge: 'primary',
    tintBg: 'bg-primary/12',
    tintText: 'text-primary',
  },
};

const fallback = categoryConfig.Atas;

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('pt-PT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function DocumentCard({
  id,
  title,
  description,
  category,
  year,
  date,
  fileSize,
  url,
}: DocumentItem) {
  const config = categoryConfig[category] ?? fallback;

  return (
    <article className="surface-card hover:border-line-strong flex h-full flex-col overflow-hidden transition-all duration-200 hover:shadow-md">
      <div className="flex flex-1 flex-col p-6">
        <div className="mb-5 flex items-start justify-between">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${config.tintBg}`}>
            <Icon name={config.icon} size="sm" className={config.tintText} />
          </div>
          <Badge label={category} color={config.badge} size="sm" />
        </div>

        <div className="flex-1">
          <h3 className="font-headline text-on-surface mb-2 text-base leading-snug font-semibold">
            {title}
          </h3>
          {description && (
            <p className="text-on-surface-light line-clamp-2 text-sm leading-relaxed">
              {description}
            </p>
          )}
        </div>

        <div className="border-line mt-4 flex items-center justify-between border-t pt-4">
          <span className="text-on-surface-light flex items-center gap-1.5 text-xs">
            <Icon name="calendar_today" size="sm" />
            {date ? formatDate(date) : '—'}
          </span>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              download
              onClick={() =>
                trackUmamiEvent('document_download', {
                  document_id: id,
                  category,
                  year,
                })
              }
              className="border-primary/30 bg-primary/8 text-primary hover:bg-primary/15 flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors duration-200"
            >
              {fileSize ?? 'PDF'}
              <Icon name="download" size="sm" />
            </a>
          ) : (
            <span className="text-on-surface-light/50 text-xs">PDF indisponível</span>
          )}
        </div>
      </div>
    </article>
  );
}
